'use client'

import { Container } from '@/shared/components/Container'
import { animate, motion } from 'framer-motion'
import React, { useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import { UserReviewSection } from '@/lib/apis/sanity'

type Props = {
  content: UserReviewSection
}

export default function ReviewSection({ content }: Props) {
  const currentSnappedReview = useRef<HTMLElement | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const reviewDivs = useRef([] as Array<HTMLElement>)
  const [currentIndex, setCurrentIndex] = useState(0)

  useEffect(() => {
    reviewDivs.current = reviewDivs.current.slice(0, content.reviews.length)

    // Need to scroll the first one to the middle
    const viewPortCenter = window.innerWidth / 2
    const firstRect = reviewDivs.current[0].getBoundingClientRect()
    const firstCenter = firstRect.left + firstRect.width / 2
    const offset = viewPortCenter - firstCenter
    animate(
      containerRef.current!,
      {
        x: offset,
      },
      {
        duration: 0,
      },
    )
  }, [content.reviews.length])

  function findTranslateX(el: HTMLElement): number {
    const style = window.getComputedStyle(el)
    const matrix = new WebKitCSSMatrix(style.transform)
    return matrix.m41
  }

  return (
    <div className="bg-gray-800">
      <Container className="py-20">
        <div className="flex flex-col items-center gap-3">
          <h2 className="font-bitter text-3xl font-medium text-white">
            {content.title}
          </h2>
          <p className="font-sans text-lg text-white">{content.subtitle}</p>
        </div>
        <div className="mt-12 w-full overflow-hidden">
          <motion.div
            ref={containerRef}
            className="flex w-fit max-w-none cursor-grab gap-x-16 active:cursor-grabbing"
            drag="x"
            dragMomentum={false}
            onDragEnd={() => {
              const viewPortCenter = window.innerWidth / 2
              let nearestIndex = 0
              let nearest: HTMLElement | null = null
              let nearestDistance = Infinity

              // TODO: We should consider the drag direction as well. If the user is dragging in right direction -
              //       the nearest one might be the content whose center has already gone pass the view port's
              //       center. And in this case, we should snap to the one after.
              for (let i = 0; i < reviewDivs.current.length; i++) {
                const current = reviewDivs.current[i]
                const rect = current.getBoundingClientRect()
                const center = rect.left + rect.width / 2
                const distance = Math.abs(viewPortCenter - center)

                // The items should become nearer as we iterate through the array.
                // Once it gets further, we no longer need to continue as we have already
                // gone past the nearest one.
                if (distance <= nearestDistance) {
                  nearest = current
                  nearestDistance = distance
                  nearestIndex = i
                } else {
                  break
                }
              }

              if (nearest === null) throw new Error('No nearest element found')

              const rect = nearest.getBoundingClientRect()
              const offset = viewPortCenter - (rect.left + rect.width / 2)
              const translateX = findTranslateX(containerRef.current!)

              animate(
                containerRef.current!,
                {
                  // Shift the current translate by offset amount
                  x: translateX + offset,
                },
                {
                  ease: ['easeOut'],
                },
              )

              animate(
                currentSnappedReview.current!,
                {
                  opacity: 0.4,
                },
                {
                  ease: ['easeOut'],
                },
              )

              animate(
                nearest,
                {
                  opacity: 1.0,
                },
                {
                  ease: ['easeOut'],
                },
              )

              currentSnappedReview.current = nearest
              setCurrentIndex(nearestIndex)
            }}
          >
            {content.reviews.map((review, index) => (
              <div
                key={index}
                className={clsx(
                  'w-80 sm:w-96',
                  index === 0 ? 'opacity-100' : 'opacity-[40%]',
                )}
                ref={(el) => {
                  // Normally we don't need to check the refs length on normal rendering as
                  // this component will most likely be rendered only once. But when developing,
                  // the rendering will be run multiple times and need to handle that here.
                  if (index < reviewDivs.current.length)
                    reviewDivs.current[index] = el!
                  else reviewDivs.current = [...reviewDivs.current, el!]

                  if (index === 0 && currentSnappedReview.current === null)
                    currentSnappedReview.current = el!
                }}
              >
                <p className="font-sans text-xl text-slate-200">
                  {'"'}
                  {review.content}
                  {'"'}
                </p>
                <p className="mt-8 font-sans text-sm text-slate-300">
                  {review.author}{' '}
                  <span className="ml-1.5">
                    - {review.city}, {review.state}
                  </span>
                </p>
              </div>
            ))}
          </motion.div>
          <div className="mt-12 flex justify-center gap-x-1.5 sm:hidden">
            {content.reviews.map((_, index) => (
              <div
                key={index}
                className={clsx(
                  'h-0.5 w-3 rounded-sm bg-white transition-opacity',
                  currentIndex === index ? 'opacity-100' : 'opacity-40',
                )}
              />
            ))}
          </div>
        </div>
      </Container>
    </div>
  )
}
